import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Tabs, Tab } from 'react-bootstrap';
import { Formik, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import Select from 'react-select';
import * as api from '~/Services/Service';
import { selectStylesSm } from '~/Styles/reactSelectStyles';
import TabHierarquia from './TabHierarquia';

const validationSchema = Yup.object().shape({
  descricao: Yup.string()
    .required('Descrição é obrigatória')
    .max(50, 'Máximo 50 caracteres'),
  codigo: Yup.number()
    .typeError('Código deve ser um número')
    .integer('Código deve ser inteiro')
    .positive('Código deve ser positivo')
    .required('Código é obrigatório'),
});

const ModalDepartamento = (props) => {
  const { show, onHide, departamento, onSalvar } = props;

  const ehEdicao = departamento != null;

  const [categorias, setCategorias] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [gruposEmpresa, setGruposEmpresa] = useState([]);
  const [faixaAtual, setFaixaAtual] = useState(null);
  const [erroFaixa, setErroFaixa] = useState('');

  useEffect(() => {
    if (show) {
      carregarOpcoes();
      setFaixaAtual(null);
      setErroFaixa('');
    }
  }, [show]);

  const carregarOpcoes = async () => {
    try {
      const [cats, usus, ges] = await Promise.all([
        api.buscarCategoriasDepartamento(),
        api.buscarUsuarioOptions('ativos'),
        api.listarGruposEmpresaAdmin(),
      ]);
      setCategorias(cats || []);
      setUsuarios(usus || []);
      setGruposEmpresa(ges || []);
    } catch (e) {
      // ignora erro de lookup, campos ficarão vazios
    }
  };

  const opsCategorias = categorias.map((c) => {
    return { value: c.codigo, label: c.descricao + ' (' + c.faixa_ini + '-' + c.faixa_fim + ')', faixa_ini: c.faixa_ini, faixa_fim: c.faixa_fim };
  });

  const opsUsuarios = usuarios.map((u) => {
    const nome = u.nome_completo ? u.nome_completo : (u.nome ? u.nome : (u.nome_usuario ? u.nome_usuario : String(u.codigo_usuario)));
    return { value: u.codigo_usuario, label: nome };
  });

  const opsGE = gruposEmpresa.map((g) => {
    const label = g.apelido ? g.apelido : (g.descricao ? g.descricao : String(g.codigo));
    return { value: g.codigo, label: label };
  });

  const getInitialValues = () => {
    if (ehEdicao) {
      return {
        codigo: departamento.codigo,
        descricao: departamento.descricao || '',
        grupo: departamento.grupo != null ? departamento.grupo : '',
        responsavel: departamento.responsavel != null ? departamento.responsavel : '',
        codigo_grupo_empresa: departamento.codigo_grupo_empresa != null ? departamento.codigo_grupo_empresa : '',
        m2: departamento.m2 != null ? departamento.m2 : '',
        rateio: departamento.rateio || '',
        ver: departamento.ver || '',
        status: departamento.status || 'A',
        workflow: departamento.workflow || '',
        antecipa_pgto: departamento.antecipa_pgto || '',
        manter_na_caixa_aprovacao: departamento.manter_na_caixa_aprovacao || '',
        assinatura_digital: departamento.assinatura_digital || '',
        assinatura_digital_auto_aprova: departamento.assinatura_digital_auto_aprova || '',
        workflow_depto_digitador: departamento.workflow_depto_digitador || '',
      };
    }
    return {
      codigo: '',
      descricao: '',
      grupo: '',
      responsavel: '',
      codigo_grupo_empresa: '',
      m2: '',
      rateio: '',
      ver: '',
      status: 'A',
      workflow: '',
      antecipa_pgto: '',
      manter_na_caixa_aprovacao: '',
      assinatura_digital: '',
      assinatura_digital_auto_aprova: '',
      workflow_depto_digitador: '',
    };
  };

  const handleSubmit = async (values, formikBag) => {
    // Validação inline de faixa antes de enviar
    if (!ehEdicao && faixaAtual) {
      const cod = Number(values.codigo);
      if (cod < faixaAtual.faixa_ini || cod > faixaAtual.faixa_fim) {
        toast.error('Código fora da faixa ' + faixaAtual.faixa_ini + '-' + faixaAtual.faixa_fim + ' da categoria');
        formikBag.setSubmitting(false);
        return;
      }
    }
    try {
      const payload = {
        codigo: Number(values.codigo),
        descricao: values.descricao,
        grupo: values.grupo !== '' ? Number(values.grupo) : null,
        responsavel: values.responsavel !== '' ? Number(values.responsavel) : null,
        codigo_grupo_empresa: values.codigo_grupo_empresa !== '' ? Number(values.codigo_grupo_empresa) : null,
        m2: values.m2 !== '' ? Number(values.m2) : null,
        rateio: values.rateio || 'N',
        ver: values.ver || null,
        status: values.status,
        workflow: values.workflow || null,
        antecipa_pgto: values.antecipa_pgto || null,
        manter_na_caixa_aprovacao: values.manter_na_caixa_aprovacao || null,
        assinatura_digital: values.assinatura_digital || null,
        assinatura_digital_auto_aprova: values.assinatura_digital_auto_aprova || null,
        workflow_depto_digitador: values.workflow_depto_digitador || null,
      };
      if (ehEdicao) {
        await api.atualizarDepartamento(payload);
        toast.success('Departamento atualizado com sucesso.');
      } else {
        await api.criarDepartamento(payload);
        toast.success('Departamento criado com sucesso.');
      }
      onSalvar();
    } catch (err) {
      toast.error((err && err.message) || 'Erro ao salvar departamento.');
    } finally {
      formikBag.setSubmitting(false);
    }
  };

  const handleSugerirCodigo = async (codigoCategoria, setFieldValue) => {
    if (!codigoCategoria) return;
    try {
      const resp = await api.sugerirCodigoDepartamento(codigoCategoria);
      const sugerido = resp && resp.codigo_sugerido != null ? resp.codigo_sugerido : '';
      setFieldValue('codigo', sugerido);
      setErroFaixa('');
    } catch (e) {
      setFieldValue('codigo', '');
    }
  };

  const validarCodigoFaixa = (codigo, faixa) => {
    if (!faixa || !codigo) {
      setErroFaixa('');
      return;
    }
    const cod = Number(codigo);
    if (isNaN(cod)) {
      setErroFaixa('');
      return;
    }
    if (cod < faixa.faixa_ini || cod > faixa.faixa_fim) {
      setErroFaixa('Código fora da faixa ' + faixa.faixa_ini + '-' + faixa.faixa_fim);
    } else {
      setErroFaixa('');
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton style={{ backgroundColor: '#0747a6', color: '#fff' }}>
        <Modal.Title style={{ fontSize: '1rem', color: '#fff' }}>
          {ehEdicao ? 'Editar Departamento' : 'Novo Departamento'}
        </Modal.Title>
      </Modal.Header>

      <Formik
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formik) => {
          const categoriaValue = formik.values.grupo !== ''
            ? opsCategorias.find((o) => { return o.value === Number(formik.values.grupo); }) || null
            : null;

          const responsavelValue = formik.values.responsavel !== ''
            ? opsUsuarios.find((o) => { return o.value === Number(formik.values.responsavel); }) || null
            : null;

          const geValue = formik.values.codigo_grupo_empresa !== ''
            ? opsGE.find((o) => { return o.value === Number(formik.values.codigo_grupo_empresa); }) || null
            : null;

          const renderFormulario = () => {
            return (
              <>
                <Row className="mb-2">
                  <Col md={6}>
                    <Form.Group className="mb-1">
                      <Form.Label as="small" className="mb-0">
                        Categoria {!ehEdicao && <span className="text-danger">*</span>}
                      </Form.Label>
                      <Select
                        options={opsCategorias}
                        value={categoriaValue}
                        onChange={(opt) => {
                          const val = opt ? opt.value : '';
                          formik.setFieldValue('grupo', val);
                          if (opt) {
                            const faixa = { faixa_ini: opt.faixa_ini, faixa_fim: opt.faixa_fim };
                            setFaixaAtual(faixa);
                            if (!ehEdicao && val) {
                              handleSugerirCodigo(val, formik.setFieldValue);
                            }
                            // Validar código atual contra nova faixa
                            if (formik.values.codigo) {
                              validarCodigoFaixa(formik.values.codigo, faixa);
                            }
                          } else {
                            setFaixaAtual(null);
                            setErroFaixa('');
                          }
                        }}
                        placeholder="Selecione..."
                        isClearable
                        isDisabled={ehEdicao}
                        styles={selectStylesSm}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-1">
                      <Form.Label as="small" className="mb-0">
                        Código <span className="text-danger">*</span>
                        {faixaAtual && !ehEdicao && (
                          <span className="text-muted ml-1" style={{ fontSize: '0.7rem' }}>
                            (faixa: {faixaAtual.faixa_ini}-{faixaAtual.faixa_fim})
                          </span>
                        )}
                      </Form.Label>
                      <Field
                        as={Form.Control}
                        type="number"
                        name="codigo"
                        size="sm"
                        readOnly={ehEdicao}
                        disabled={ehEdicao}
                        style={ehEdicao ? { backgroundColor: '#f5f5f5' } : {}}
                        isInvalid={(formik.touched.codigo && !!formik.errors.codigo) || !!erroFaixa}
                        onChange={(e) => {
                          formik.handleChange(e);
                          validarCodigoFaixa(e.target.value, faixaAtual);
                        }}
                      />
                      <ErrorMessage name="codigo" component="div" className="invalid-feedback d-block" style={{ fontSize: '0.75rem' }} />
                      {erroFaixa && (
                        <div className="text-danger" style={{ fontSize: '0.75rem' }}>
                          {erroFaixa}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-2">
                  <Col md={8}>
                    <Form.Group className="mb-1">
                      <Form.Label as="small" className="mb-0">
                        Descrição <span className="text-danger">*</span>
                      </Form.Label>
                      <Field
                        as={Form.Control}
                        type="text"
                        name="descricao"
                        size="sm"
                        maxLength={50}
                        isInvalid={formik.touched.descricao && !!formik.errors.descricao}
                      />
                      <ErrorMessage name="descricao" component="div" className="invalid-feedback d-block" style={{ fontSize: '0.75rem' }} />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-1">
                      <Form.Label as="small" className="mb-0">Status</Form.Label>
                      <Field as={Form.Control} name="status" size="sm" custom>
                        {(fieldProps) => {
                          return (
                            <Form.Control as="select" size="sm" {...fieldProps.field}>
                              <option value="A">Ativo</option>
                              <option value="I">Inativo</option>
                            </Form.Control>
                          );
                        }}
                      </Field>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-2">
                  <Col md={6}>
                    <Form.Group className="mb-1">
                      <Form.Label as="small" className="mb-0">Responsável</Form.Label>
                      <Select
                        options={opsUsuarios}
                        value={responsavelValue}
                        onChange={(opt) => { formik.setFieldValue('responsavel', opt ? opt.value : ''); }}
                        placeholder="Selecione..."
                        isClearable
                        styles={selectStylesSm}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-1">
                      <Form.Label as="small" className="mb-0">Grupo Empresa</Form.Label>
                      <Select
                        options={opsGE}
                        value={geValue}
                        onChange={(opt) => { formik.setFieldValue('codigo_grupo_empresa', opt ? opt.value : ''); }}
                        placeholder="Selecione..."
                        isClearable
                        styles={selectStylesSm}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-2">
                  <Col md={4}>
                    <Form.Group className="mb-1">
                      <Form.Label as="small" className="mb-0">m²</Form.Label>
                      <Field
                        as={Form.Control}
                        type="number"
                        name="m2"
                        size="sm"
                        step="0.000001"
                        min="0"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-1">
                      <Form.Label as="small" className="mb-0">Rateio</Form.Label>
                      <Field name="rateio">
                        {(fieldProps) => {
                          return (
                            <Form.Control as="select" size="sm" {...fieldProps.field}>
                              <option value="">Não</option>
                              <option value="S">Sim</option>
                            </Form.Control>
                          );
                        }}
                      </Field>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-1">
                      <Form.Label as="small" className="mb-0">Visualiza</Form.Label>
                      <Field name="ver">
                        {(fieldProps) => {
                          return (
                            <Form.Control as="select" size="sm" {...fieldProps.field}>
                              <option value="">Não</option>
                              <option value="S">Sim</option>
                            </Form.Control>
                          );
                        }}
                      </Field>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-2">
                  <Col md={4}>
                    <Form.Group className="mb-1">
                      <Form.Label as="small" className="mb-0">Participa de Workflow</Form.Label>
                      <Field name="workflow">
                        {(fieldProps) => {
                          return (
                            <Form.Control as="select" size="sm" {...fieldProps.field}>
                              <option value="">Não</option>
                              <option value="S">Sim</option>
                            </Form.Control>
                          );
                        }}
                      </Field>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-1">
                      <Form.Label as="small" className="mb-0">Antecipa Pagamento</Form.Label>
                      <Field name="antecipa_pgto">
                        {(fieldProps) => {
                          return (
                            <Form.Control as="select" size="sm" {...fieldProps.field}>
                              <option value="">Não</option>
                              <option value="S">Sim</option>
                            </Form.Control>
                          );
                        }}
                      </Field>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-1">
                      <Form.Label as="small" className="mb-0">Manter na Caixa de Aprovação</Form.Label>
                      <Field name="manter_na_caixa_aprovacao">
                        {(fieldProps) => {
                          return (
                            <Form.Control as="select" size="sm" {...fieldProps.field}>
                              <option value="">Não</option>
                              <option value="S">Sim</option>
                            </Form.Control>
                          );
                        }}
                      </Field>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="align-items-end mb-2">
                  <Col md={4} className="d-flex flex-column justify-content-end">
                    <Form.Group className="mb-1">
                      <Form.Label as="small" className="mb-0">Workflow Depto Digitador</Form.Label>
                      <Field name="workflow_depto_digitador">
                        {(fieldProps) => {
                          return (
                            <Form.Control as="select" size="sm" {...fieldProps.field}>
                              <option value="">Não</option>
                              <option value="S">Sim</option>
                            </Form.Control>
                          );
                        }}
                      </Field>
                    </Form.Group>
                  </Col>
                  <Col md={8}>
                    <fieldset className="border rounded px-3 pb-2 pt-1" style={{ borderColor: '#dee2e6' }}>
                      <legend className="w-auto px-2 mb-0" style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#495057' }}>
                        Assinatura Digital
                      </legend>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-1">
                            <Form.Label as="small" className="mb-0">Listar Depto como Testemunha</Form.Label>
                            <Field name="assinatura_digital">
                              {(fieldProps) => {
                                return (
                                  <Form.Control as="select" size="sm" {...fieldProps.field}>
                                    <option value="">Não</option>
                                    <option value="S">Sim</option>
                                  </Form.Control>
                                );
                              }}
                            </Field>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-1">
                            <Form.Label as="small" className="mb-0">Aprovação Automática</Form.Label>
                            <Field name="assinatura_digital_auto_aprova">
                              {(fieldProps) => {
                                return (
                                  <Form.Control as="select" size="sm" {...fieldProps.field}>
                                    <option value="">Não</option>
                                    <option value="S">Sim</option>
                                  </Form.Control>
                                );
                              }}
                            </Field>
                          </Form.Group>
                        </Col>
                      </Row>
                    </fieldset>
                  </Col>
                </Row>
              </>
            );
          };

          return (
            <Form onSubmit={formik.handleSubmit}>
              <Modal.Body>
                {ehEdicao ? (
                  <Tabs defaultActiveKey="configuracoes" id="depto-tabs">
                    <Tab eventKey="configuracoes" title="Configurações">
                      <div className="pt-3">
                        {renderFormulario()}
                      </div>
                    </Tab>
                    <Tab eventKey="hierarquia" title="Hierarquia">
                      <div className="pt-3">
                        <TabHierarquia codigoDepartamento={departamento.codigo} />
                      </div>
                    </Tab>
                  </Tabs>
                ) : (
                  renderFormulario()
                )}
              </Modal.Body>

              <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={formik.isSubmitting}>
                  Cancelar
                </Button>
                <Button variant="primary" type="submit" disabled={formik.isSubmitting || !!erroFaixa}>
                  {formik.isSubmitting ? 'Salvando...' : 'Salvar'}
                </Button>
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default ModalDepartamento;
